import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Package, Eye, RefreshCw, Clock, CheckCircle, AlertCircle, XCircle, ThumbsUp, ThumbsDown, X, FileText, Image as ImageIcon, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending_review: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  confirmando: { label: 'Confirmando API', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: RefreshCw },
  completado: { label: 'Completado', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle },
  rejected: { label: 'Rechazado', color: 'bg-red-100 text-red-700 border-red-300', icon: XCircle },
  api_error: { label: 'Error API', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: AlertCircle },
  api_submitted: { label: 'Enviado a API', color: 'bg-indigo-100 text-indigo-700 border-indigo-300', icon: Package },
};

const AdminB2BOrdersPage = () => {
  const { getAuthHeaders } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [filter, setFilter] = useState('all');

  const loadOrders = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const res = await axios.get(`${API}/admin/b2b/orders`, { headers: getAuthHeaders(), params });
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Error al cargar ordenes');
      setOrders([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadOrders(); }, [filter]);

  const approveOrder = async (orderId) => {
    setActionLoading(prev => ({ ...prev, [orderId]: 'approve' }));
    try {
      const res = await axios.put(`${API}/admin/b2b/orders/${orderId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      loadOrders();
      setSelectedOrder(null);
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setActionLoading(prev => ({ ...prev, [orderId]: null })); }
  };

  const rejectOrder = async (orderId) => {
    if (!window.confirm('Rechazar esta orden?')) return;
    setActionLoading(prev => ({ ...prev, [orderId]: 'reject' }));
    try {
      await axios.put(`${API}/admin/b2b/orders/${orderId}/reject`, {}, { headers: getAuthHeaders() });
      toast.success('Orden rechazada');
      loadOrders();
      setSelectedOrder(null);
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setActionLoading(prev => ({ ...prev, [orderId]: null })); }
  };

  const pendingCount = orders.filter(o => o.status === 'pending_review').length;

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="b2b-orders-loading">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-b2b-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Ordenes B2B</h1>
          <p className="text-sm text-slate-500 mt-1">
            {pendingCount > 0 ? <span className="text-amber-600 font-medium">{pendingCount} pendiente{pendingCount > 1 ? 's' : ''} de aprobacion</span> : 'Revisa y aprueba las solicitudes de transferencia'}
          </p>
        </div>
        <Button onClick={() => { setLoading(true); loadOrders(); }} variant="outline" size="sm" data-testid="refresh-orders-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap" data-testid="order-filters">
        {[
          { val: 'all', label: 'Todas' },
          { val: 'pending_review', label: `Pendientes (${pendingCount})` },
          { val: 'confirmando', label: 'Confirmando' },
          { val: 'rejected', label: 'Rechazadas' },
        ].map(f => (
          <Button key={f.val} size="sm" variant={filter === f.val ? 'default' : 'outline'} onClick={() => setFilter(f.val)} data-testid={`filter-${f.val}`}>
            {f.label}
          </Button>
        ))}
      </div>

      {orders.length === 0 ? (
        <Card className="p-12 text-center" data-testid="no-orders">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600">No hay ordenes</h3>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {orders.map((order) => {
            const st = statusConfig[order.status] || statusConfig.pending_review;
            const StatusIcon = st.icon;
            const isPending = order.status === 'pending_review';
            return (
              <Card key={order.id} className={`p-4 ${isPending ? 'border-amber-300 bg-amber-50/30' : ''}`} data-testid={`order-card-${order.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Order Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${st.color}`}>
                        <StatusIcon className="w-3 h-3" /> {st.label}
                      </span>
                      <span className="text-xs text-slate-400 font-mono">{order.id.slice(0, 8)}...</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <span className="font-medium text-slate-800">{order.user_name || 'N/A'}</span>
                      <span className="text-slate-400">({order.user_role})</span>
                      <span className="capitalize font-medium text-blue-700">{order.channel}</span>
                      <span className="font-bold text-slate-800">${Number(order.amount).toFixed(2)}</span>
                      <span className="text-amber-600">+${Number(order.admin_fee || 0).toFixed(2)} fee</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 text-xs text-slate-400 mt-1">
                      <span>{new Date(order.created_at).toLocaleString()}</span>
                      {order.receiver_data?.full_name && <span>Para: {order.receiver_data.full_name}</span>}
                      {order.payment_method_name && <span>Via: {order.payment_method_name}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(order)} data-testid={`view-order-${order.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {isPending && (
                      <>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approveOrder(order.id)} disabled={!!actionLoading[order.id]} data-testid={`approve-${order.id}`}>
                          {actionLoading[order.id] === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4 mr-1" />}
                          Aprobar
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => rejectOrder(order.id)} disabled={!!actionLoading[order.id]} data-testid={`reject-${order.id}`}>
                          {actionLoading[order.id] === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4 mr-1" />}
                          Rechazar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedOrder(null)} data-testid="order-detail-modal">
          <Card className="w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-slate-800">Detalle de Orden</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(null)}><X className="w-4 h-4" /></Button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div><span className="text-slate-500">ID:</span> <span className="font-mono text-xs">{selectedOrder.id}</span></div>
              <div><span className="text-slate-500">Estado:</span> <span className="font-medium">{(statusConfig[selectedOrder.status] || {}).label || selectedOrder.status}</span></div>
              <div><span className="text-slate-500">Usuario:</span> <span className="font-medium">{selectedOrder.user_name} ({selectedOrder.user_role})</span></div>
              <div><span className="text-slate-500">Canal:</span> <span className="font-medium capitalize">{selectedOrder.channel}</span></div>
              <div><span className="text-slate-500">Monto:</span> <span className="font-bold">${Number(selectedOrder.amount).toFixed(2)}</span></div>
              <div><span className="text-slate-500">Fee admin:</span> <span className="text-amber-600">${Number(selectedOrder.admin_fee || 0).toFixed(2)}</span></div>
              <div><span className="text-slate-500">Total pagado:</span> <span className="font-bold text-blue-700">${Number(selectedOrder.total_to_pay || 0).toFixed(2)}</span></div>
              <div><span className="text-slate-500">Metodo pago:</span> <span>{selectedOrder.payment_method_name || 'N/A'}</span></div>
              <div><span className="text-slate-500">Fecha:</span> <span>{new Date(selectedOrder.created_at).toLocaleString()}</span></div>
              {selectedOrder.receiver_data && (
                <div className="col-span-2"><span className="text-slate-500">Receptor:</span> <span className="font-medium">{selectedOrder.receiver_data.full_name} {selectedOrder.receiver_data.phone ? `(${selectedOrder.receiver_data.phone})` : ''}</span></div>
              )}
            </div>

            {/* Proof Image */}
            {selectedOrder.proof_image && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1"><FileText className="w-4 h-4" /> Comprobante de pago</h4>
                {selectedOrder.proof_image.endsWith('.pdf') ? (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${selectedOrder.proof_image}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm font-medium">
                    <FileText className="w-4 h-4" /> Ver PDF
                  </a>
                ) : (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${selectedOrder.proof_image}`} alt="Comprobante" className="max-h-64 rounded-lg border shadow-sm" data-testid="proof-image" />
                )}
              </div>
            )}

            {/* API Response */}
            {selectedOrder.api_response && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Respuesta API</h4>
                <pre className="bg-slate-50 rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap max-h-40">{JSON.stringify(selectedOrder.api_response, null, 2)}</pre>
              </div>
            )}

            {/* Actions */}
            {selectedOrder.status === 'pending_review' && (
              <div className="flex gap-3 pt-4 border-t">
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approveOrder(selectedOrder.id)} disabled={!!actionLoading[selectedOrder.id]} data-testid="modal-approve-btn">
                  {actionLoading[selectedOrder.id] === 'approve' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ThumbsUp className="w-4 h-4 mr-2" />}
                  Aprobar y Enviar a API
                </Button>
                <Button variant="destructive" className="flex-1" onClick={() => rejectOrder(selectedOrder.id)} disabled={!!actionLoading[selectedOrder.id]} data-testid="modal-reject-btn">
                  {actionLoading[selectedOrder.id] === 'reject' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ThumbsDown className="w-4 h-4 mr-2" />}
                  Rechazar
                </Button>
              </div>
            )}
            {selectedOrder.status !== 'pending_review' && selectedOrder.status !== 'rejected' && (
              <div className="pt-4 border-t">
                <a href={`${API}/b2b/orders/${selectedOrder.id}/receipt`} target="_blank" rel="noreferrer" data-testid="modal-download-receipt">
                  <Button variant="outline" className="w-full">
                    <Download className="w-4 h-4 mr-2" /> Descargar Recibo PDF
                  </Button>
                </a>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminB2BOrdersPage;
